// A guardrail's form (03-the-guardrails-service.md § the console: the
// selector, the expression in a monospace field, the escalation block and the
// transformer block as forms, and the service's refusal shown beside the
// field it names). The
// same form creates a rule in *Add a guardrail*, in the page's place, and
// changes one on its page. In *Add a guardrail* the escalation's details — how long it waits, how long an
// approval is good for, what is shown — sit under *More options*, closed,
// since each has a working default (01-the-console.md § conventions:
// density and length); a refusal naming one of them opens it. The transformer
// block follows the same rule: its name always shown, its leg timeout — five
// seconds unless changed — under *More options* in *Add a guardrail*.

import { useState, type ReactNode } from "react";

import { Field, TextInput } from "../../ui/components.tsx";
import {
  Approver,
  DEFAULT_TRANSFORM_TIMEOUT,
  fieldOf,
  validDuration,
  validID,
  type Escalation,
  type Rule,
  type Transformer,
} from "../../wire/guard.ts";
import { MAX_NAME, validName } from "../../wire/inference.ts";
import "../../ui/density.css";
import { BLANK } from "./starters.ts";
import { canEscalate, canTransform, guards, ruleField, type RuleField } from "./words.ts";

/**
 * INPUTS are the names an expression reads (01-rules-and-enforcement.md § the
 * expression language): `findings` is what a model's required inspection
 * found, and the last names the inspection that answered.
 */
const INPUTS = ["subject", "principal", "caller", "args", "request", "time", "findings", "callout"] as const;

/** Draft is the form's state: the rule's fields as typed. */
export interface Draft {
  id: string;
  selector: string;
  expression: string;
  approver: string;
  ttl: string;
  grantTtl: string;
  context: string;
  /** The transformer's registration name. */
  transformer: string;
  /** Each leg's timeout; empty leaves it to the service's default. */
  transformTimeout: string;
  enabled: boolean;
}

/** draftOf is the form filled from a rule. */
export function draftOf(r: Rule): Draft {
  return {
    id: r.id,
    selector: r.selector,
    expression: r.expression,
    approver: r.escalation?.approver ?? Approver.self,
    ttl: r.escalation?.ttl ?? "24h",
    grantTtl: r.escalation?.["grant-ttl"] ?? "1h",
    context: (r.escalation?.context ?? []).join(", "),
    transformer: r.transformer?.name ?? "",
    transformTimeout: r.transformer?.timeout ?? DEFAULT_TRANSFORM_TIMEOUT,
    enabled: r.enabled,
  };
}

/**
 * ruleOf composes the document from the form: the escalation block present
 * exactly when the expression can escalate, the transformer block exactly
 * when it can transform — its timeout left out when the field is empty, which
 * the service fills with the default.
 */
export function ruleOf(d: Draft): Rule {
  const r: Rule = {
    id: d.id.trim(),
    selector: d.selector.trim(),
    expression: d.expression.trim(),
    enabled: d.enabled,
  };
  if (canEscalate(r.expression)) {
    const e: Escalation = {
      approver: d.approver === Approver.admin ? Approver.admin : Approver.self,
      ttl: d.ttl.trim(),
      "grant-ttl": d.grantTtl.trim(),
    };
    const context = d.context
      .split(/[\s,]+/)
      .map((c) => c.trim())
      .filter((c) => c !== "");
    if (context.length > 0) {
      e.context = context;
    }
    r.escalation = e;
  }
  if (canTransform(r.expression)) {
    const t: Transformer = { name: d.transformer.trim() };
    const timeout = d.transformTimeout.trim();
    if (timeout !== "") {
      t.timeout = timeout;
    }
    r.transformer = t;
  }
  return r;
}

/** problems says what the form itself can see is wrong, before the service is asked; the service's validation is the authority. */
export function problems(d: Draft): Partial<Record<RuleField, string>> {
  const out: Partial<Record<RuleField, string>> = {};
  if (d.id.trim() !== "" && !validID(d.id.trim())) {
    out.id = "a name is one word with no space, dot, * or >, and not list, watch or keys";
  }
  if (d.selector.trim() !== "" && !d.selector.trim().startsWith("workload.")) {
    out.selector = "what a guardrail guards is under workload.: the platform's own services are not guarded";
  }
  if (canEscalate(d.expression)) {
    if (d.ttl.trim() !== "" && !validDuration(d.ttl.trim())) {
      out["escalation.ttl"] = "a duration such as 30m, 24h or 1h30m";
    }
    if (d.grantTtl.trim() !== "" && !validDuration(d.grantTtl.trim())) {
      out["escalation.grant-ttl"] = "a duration such as 30m, 24h or 1h30m";
    }
  }
  if (canTransform(d.expression)) {
    if (d.transformer.trim() !== "" && !validName(d.transformer.trim())) {
      out["transformer.name"] =
        `a transformer's name is letters, digits, dot, dash or underscore, at most ${String(MAX_NAME)} characters`;
    }
    if (d.transformTimeout.trim() !== "" && !validDuration(d.transformTimeout.trim())) {
      out["transformer.timeout"] = "a duration such as 5s, 30s or 1m";
    }
  }
  return out;
}

/** refusalFields reads the service's refusal as the field it names and the reason, verbatim. */
export function refusalFields(err: unknown): Partial<Record<RuleField, string>> {
  const message = err instanceof Error ? err.message : String(err);
  const f = fieldOf(message);
  if (f === undefined) {
    return {};
  }
  const field = ruleField(f.field);
  return field === undefined ? {} : { [field]: f.reason };
}

export function RuleForm({
  draft,
  onChange,
  refused,
  editing,
  onSubmit,
}: {
  draft: Draft;
  onChange: (d: Draft) => void;
  /** The service's refusal, shown beside the field it names. */
  refused: Partial<Record<RuleField, string>>;
  /** Whether the rule exists: its name is then fixed. */
  editing: boolean;
  onSubmit: () => void;
}) {
  const [touched, setTouched] = useState(false);
  const own = problems(draft);
  const beside = (f: RuleField): string | undefined => refused[f] ?? (touched ? own[f] : undefined);
  const set = (patch: Partial<Draft>) => {
    setTouched(true);
    onChange({ ...draft, ...patch });
  };
  const escalates = canEscalate(draft.expression);
  const transforms = canTransform(draft.expression);
  const hint = (f: RuleField, plain: ReactNode) => {
    const b = beside(f);
    return b === undefined ? plain : <span className="bad">{b}</span>;
  };
  return (
    <form
      className="stack"
      onSubmit={(e) => {
        e.preventDefault();
        onSubmit();
      }}
    >
      <Field label="Name" hint={hint("id", "One word; every outcome names it.")}>
        <TextInput
          value={draft.id}
          onChange={(id) => {
            set({ id });
          }}
          placeholder="confirm-deletions"
          mono
          required
          disabled={editing}
          autoFocus={!editing}
        />
      </Field>
      <Field
        label="What it guards"
        hint={hint(
          "selector",
          draft.selector.trim() === ""
            ? "A pattern over what workloads serve: workload.<id>.tool.<tool>, workload.<id>.infer.<capability>, workload.<id>.<verb>; * is any one part, > the rest."
            : `Guards ${guards(draft.selector.trim())}.`,
        )}
      >
        <TextInput
          value={draft.selector}
          onChange={(selector) => {
            set({ selector });
          }}
          placeholder={BLANK.selector}
          mono
          required
        />
      </Field>
      <div className="field">
        <span className="label">The rule</span>
        <textarea
          className="input mono"
          rows={4}
          value={draft.expression}
          required
          spellCheck={false}
          onChange={(e) => {
            set({ expression: e.target.value });
          }}
        />
        <span className="hint">
          {hint(
            "expression",
            <>
              Concludes &apos;allow&apos;, &apos;deny&apos;, &apos;escalate&apos; or &apos;transform&apos;:
              escalate means it needs confirmation, transform hands the call to a transformer first. It may
              read{" "}
              {INPUTS.map((input, i) => (
                <span key={input}>
                  <span className="mono">{input}</span>
                  {i < INPUTS.length - 2 ? ", " : i === INPUTS.length - 2 ? " and " : "."}
                </span>
              ))}
            </>,
          )}
        </span>
      </div>
      {beside("escalation") !== undefined && <p className="small bad">{beside("escalation")}</p>}
      {escalates && (
        <div className="card stack" style={{ padding: 16 }}>
          <h3>Needs confirmation</h3>
          <div className="field">
            <span className="label">Who confirms</span>
            <div className="choices" role="radiogroup">
              <label>
                <input
                  type="radio"
                  name="approver"
                  value={Approver.self}
                  checked={draft.approver !== Approver.admin}
                  onChange={() => {
                    set({ approver: Approver.self });
                  }}
                />
                <span className="what">
                  <span>The person who asked</span>
                  <span className="hint">You asked for this; did you mean it?</span>
                </span>
              </label>
              <label>
                <input
                  type="radio"
                  name="approver"
                  value={Approver.admin}
                  checked={draft.approver === Approver.admin}
                  onChange={() => {
                    set({ approver: Approver.admin });
                  }}
                />
                <span className="what">
                  <span>An admin</span>
                  <span className="hint">Any admin may approve or deny it.</span>
                </span>
              </label>
            </div>
            {beside("escalation.approver") !== undefined && (
              <span className="hint bad">{beside("escalation.approver")}</span>
            )}
          </div>
          {editing ? (
            <EscalationDetails draft={draft} set={set} hint={hint} />
          ) : (
            <details className="more-options" open={detailsRefused(refused) ? true : undefined}>
              <summary className="small">More options</summary>
              <EscalationDetails draft={draft} set={set} hint={hint} />
            </details>
          )}
        </div>
      )}
      {beside("transformer") !== undefined && <p className="small bad">{beside("transformer")}</p>}
      {transforms && (
        <div className="card stack" style={{ padding: 16 }}>
          <h3>Hands the call to a transformer</h3>
          <Field
            label="Transformer"
            hint={hint(
              "transformer.name",
              "The name the transformer was added under. The call passes through it on the way in, and the reply on the way out.",
            )}
          >
            <TextInput
              value={draft.transformer}
              onChange={(transformer) => {
                set({ transformer });
              }}
              placeholder="pseudonymiser"
              mono
              required
            />
          </Field>
          {editing ? (
            <TransformerDetails draft={draft} set={set} hint={hint} />
          ) : (
            <details
              className="more-options"
              open={refused["transformer.timeout"] !== undefined ? true : undefined}
            >
              <summary className="small">More options</summary>
              <TransformerDetails draft={draft} set={set} hint={hint} />
            </details>
          )}
        </div>
      )}
      <label className="checkbox">
        <input
          type="checkbox"
          checked={draft.enabled}
          onChange={(e) => {
            set({ enabled: e.target.checked });
          }}
        />
        <span>Switched on: it decides. Off keeps the text and decides nothing.</span>
      </label>
    </form>
  );
}

/** detailsRefused says whether the service's refusal names one of the escalation's details, which then shows open. */
function detailsRefused(refused: Partial<Record<RuleField, string>>): boolean {
  return (
    refused["escalation.ttl"] !== undefined ||
    refused["escalation.grant-ttl"] !== undefined ||
    refused["escalation.context"] !== undefined
  );
}

/** EscalationDetails is how long a request waits, how long an approval is good for, and what whoever confirms is shown. */
function EscalationDetails({
  draft,
  set,
  hint,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
  hint: (f: RuleField, plain: ReactNode) => ReactNode;
}) {
  return (
    <div className="stack">
      <div className="two">
        <Field label="Waits for" hint={hint("escalation.ttl", "How long the request waits for an answer.")}>
          <TextInput
            value={draft.ttl}
            onChange={(ttl) => {
              set({ ttl });
            }}
            placeholder="24h"
            mono
            required
          />
        </Field>
        <Field
          label="An approval is good for"
          hint={hint("escalation.grant-ttl", "How long the person has to ask again.")}
        >
          <TextInput
            value={draft.grantTtl}
            onChange={(grantTtl) => {
              set({ grantTtl });
            }}
            placeholder="1h"
            mono
            required
          />
        </Field>
      </div>
      <Field
        label="Shown to whoever confirms"
        hint={hint(
          "escalation.context",
          "What the request carried, by name: args, args.repository, principal.display. Separated by commas.",
        )}
      >
        <TextInput
          value={draft.context}
          onChange={(context) => {
            set({ context });
          }}
          placeholder="args, principal.display"
          mono
        />
      </Field>
    </div>
  );
}

/** TransformerDetails is how long the transformer has to answer each leg. */
function TransformerDetails({
  draft,
  set,
  hint,
}: {
  draft: Draft;
  set: (patch: Partial<Draft>) => void;
  hint: (f: RuleField, plain: ReactNode) => ReactNode;
}) {
  return (
    <Field
      label="Waits for the transformer"
      hint={hint(
        "transformer.timeout",
        "How long the transformer has to answer, each way; past it the call is refused. 5s when empty.",
      )}
    >
      <TextInput
        value={draft.transformTimeout}
        onChange={(transformTimeout) => {
          set({ transformTimeout });
        }}
        placeholder={DEFAULT_TRANSFORM_TIMEOUT}
        mono
      />
    </Field>
  );
}
